'use client';

import { useTranslations } from 'next-intl';
import { ComponentProps, memo, useCallback, useContext, useEffect, type ReactNode } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';
import { CART_STORAGE_KEY, CartContext, CartEmpty, useCartItems, type CartFormValues } from '@/entities/cart';
import { ErrorBoundary } from '@/shared/ui/error-boundary';
import { ErrorFallback } from '@/shared/ui/error-boundary/error-fallback';
import { Form } from '@/shared/ui/form';
import { Layout } from '@/shared/ui/layout';
import './shell.scss';

import { LucideLoader2 } from 'lucide-react';

type Props = ComponentProps<typeof Form>;

/** Isolates items subscription so qty/note edits don't re-render the whole cart form. */
const CartEmptyGate = ({ children }: { children: ReactNode }) => {
    const items = useCartItems();
    if (!items.length) {
        return (
            <Layout
                media={{
                    initial: {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexGrow: 1,
                    },
                }}
            >
                <CartEmpty />
            </Layout>
        );
    }
    return children;
};

type FilledFormProps = {
    formMethods: UseFormReturn<CartFormValues>;
    handleCartErrorReset: () => void;
    errorDescription: string;
} & Omit<ComponentProps<typeof Form>, keyof UseFormReturn<CartFormValues>>;

const CartFilledForm = memo(function CartFilledForm({
    formMethods,
    handleCartErrorReset,
    errorDescription,
    ...formProps
}: FilledFormProps) {
    return (
        <ErrorBoundary
            sentryTags={{ area: 'cart' }}
            onReset={handleCartErrorReset}
            fallback={({ reset }) => <ErrorFallback onReset={reset} description={errorDescription} />}
        >
            <Form data-testid="cart-form" {...formMethods} {...formProps} />
        </ErrorBoundary>
    );
});

export const Cart = (props: Props) => {
    const t = useTranslations('ERROR_BOUNDARY');
    const { formMethods, getCartFormDefaultValues } = useContext(CartContext);

    // Recovery for render crashes inside the cart (e.g. an item whose menu/price data
    // is no longer the expected shape). Clearing localStorage alone is not enough — the
    // form still holds the bad items in memory below CartProvider — so we also reset the
    // form to defaults, which lets the in-place retry re-render without the offending data.
    const handleCartErrorReset = useCallback(() => {
        localStorage.removeItem(CART_STORAGE_KEY);
        formMethods.reset(getCartFormDefaultValues());
    }, [formMethods, getCartFormDefaultValues]);
    const [isHydrated, isOrderCreated] = useWatch({
        control: formMethods.control,
        name: ['_isHydrated', '_isOrderCreated'],
    });

    // Clear on remount (user returned to cart). Do not clear on unmount — soft navigate to
    // order landing must still read `_isOrderCreated` from the surviving CartProvider.
    useEffect(() => {
        if (formMethods.getValues('_isOrderCreated')) {
            formMethods.setValue('_isOrderCreated', false);
        }
    }, [formMethods]);

    if (!isHydrated || isOrderCreated) {
        return (
            <Layout
                className="cart-shell-redirect"
                media={{
                    initial: {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexGrow: 1,
                    },
                }}
                data-testid="cart-order-redirecting"
            >
                <LucideLoader2 size={32} className="cart-shell-redirect__spinner" />
            </Layout>
        );
    }

    return (
        <CartEmptyGate>
            <CartFilledForm
                formMethods={formMethods}
                handleCartErrorReset={handleCartErrorReset}
                errorDescription={t('CART_DESCRIPTION')}
                {...props}
            />
        </CartEmptyGate>
    );
};
